import React, { useState, useEffect } from 'react';
import './ElectorateSearch.css';
import VoteBar from './VoteBar';
import electorateData from '../data/electorates.json';
import ElectoratePrediction from './ElectoratePrediction';

function ElectorateSearch({ onSearch, onElectorateSelect, currentSelectedElectorate }) {
  const [electorates, setElectorates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState({ electorates: [], suburbs: [] });
  const [localSelectedElectorate, setLocalSelectedElectorate] = useState(null);

  useEffect(() => {
    setElectorates(electorateData);
  }, []);

  // Sync with parent's selected electorate
  useEffect(() => {
    if (!currentSelectedElectorate) {
      setLocalSelectedElectorate(null);
      return;
    }
    
    // Find the full electorate data for the selected name
    const foundElectorate = electorates.find(e => e.Name === currentSelectedElectorate);
    setLocalSelectedElectorate(foundElectorate || null);
  }, [currentSelectedElectorate, electorates]);

  useEffect(() => {
    // Clear results immediately when search term changes
    setSearchResults({ electorates: [], suburbs: [] });

    if (!searchTerm) {
      return;
    }

    const term = searchTerm.toLowerCase().trim();
    
    // Search electorates by name
    const matchingElectorates = electorates.filter(electorate =>
      electorate && electorate.Name && electorate.Name.toLowerCase().startsWith(term)
    );

    // Search suburbs
    const matchingSuburbs = [];
    electorates.forEach(electorate => {
      if (electorate.Locations) {
        electorate.Locations.forEach(location => {
          if (location.Localities) {
            location.Localities.forEach(suburb => {
              const suburbLower = suburb.toLowerCase();
              if (suburbLower.startsWith(term)) {
                matchingSuburbs.push({
                  suburb,
                  electorate: electorate.Name,
                  electorateData: electorate
                });
              }
            });
          }
        });
      }
    });

    setSearchResults({
      electorates: matchingElectorates,
      suburbs: matchingSuburbs
    });
  }, [searchTerm, electorates]);

  const formatVotes = (votes) => {
    return votes?.toLocaleString() || 'N/A';
  };

  const formatSwing = (swing) => {
    if (swing === undefined || swing === null) return 'N/A';
    const sign = swing >= 0 ? '+' : '';
    return `${sign}${swing.toFixed(1)}%`;
  };

  const cleanDescription = (html) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const listItems = tempDiv.getElementsByTagName('li');
    for (let item of listItems) {
      let text = item.textContent.trim();
      text = text.replace(/,\s*and\s*$/, '');
      text = text.replace(/,\s*$/, '');
      text = text.replace(/^and\s+/, '');
      text = text.replace(/\s+and\s+/, '');
      text = text.replace(/\.\s*$/, '');
      text = text.replace(/^the\s+/, '');
      item.textContent = text;
    }
    return tempDiv.innerHTML;
  };

  const handleElectorateSelect = (electorate) => {
    setSearchTerm('');
    setSearchResults({ electorates: [], suburbs: [] });
    onElectorateSelect(electorate.Name);
  };

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    onSearch(value);
  };

  return (
    <div className="electorate-search">
      <h2>Find Your Federal Electorate</h2>
      <div className="search-container">
        <input
          type="text"
          placeholder="Enter electorate name or suburb..."
          value={searchTerm}
          onChange={handleSearchInput}
          className="search-input"
        />
      </div>

      {searchTerm && (
        <div className="results-container">
          {searchResults.electorates.length > 0 && (
            <div className="results-section">
              <h3>Matching Electorates:</h3>
              <ul className="results-list">
                {searchResults.electorates.map(electorate => (
                  <li
                    key={electorate.ShortName}
                    onClick={() => handleElectorateSelect(electorate)}
                    className="result-item"
                  >
                    {electorate.Name}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {searchResults.suburbs.length > 0 && (
            <div className="results-section">
              <h3>Matching Suburbs:</h3>
              <ul className="results-list">
                {searchResults.suburbs.map(({ suburb, electorate, electorateData }, index) => (
                  <li
                    key={`${suburb}-${electorate}-${index}`}
                    onClick={() => handleElectorateSelect(electorateData)}
                    className="result-item suburb-item"
                  >
                    <span className="suburb-name">{suburb}</span>
                    <span className="electorate-name">in {electorate}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {localSelectedElectorate && !searchTerm && (
        <div className="electorate-details">
          <h3>{localSelectedElectorate.Name}</h3>
          <div className="details-grid">
            <div className="basic-details">
              <div className="detail-item">
                <strong>State</strong>
                <p>{localSelectedElectorate.State}</p>
              </div>
              <div className="detail-item">
                <strong>Population</strong>
                <p>{localSelectedElectorate.Enrollment?.toLocaleString() || 'N/A'}</p>
              </div>
              <div className="detail-item">
                <strong>Area (km²)</strong>
                <p>{localSelectedElectorate.Area?.toLocaleString() || 'N/A'}</p>
              </div>
            </div>

            <div className="prediction-section">
              <ElectoratePrediction electorateName={localSelectedElectorate.Name} />
            </div>

            {localSelectedElectorate.TwoCandidatePreferred && (
              <div className="detail-item full-width">
                <strong>Two Candidate Preferred Results from 2022</strong>
                <div className="vote-bars">
                  <VoteBar
                    votes={localSelectedElectorate.TwoCandidatePreferred.Elected.Votes}
                    totalVotes={localSelectedElectorate.TwoCandidatePreferred.TotalVotes}
                    partyCode={localSelectedElectorate.TwoCandidatePreferred.Elected.PartyCode || 'IND'}
                    otherVotes={localSelectedElectorate.TwoCandidatePreferred.Other?.Votes}
                    otherPartyCode={localSelectedElectorate.TwoCandidatePreferred.Other?.PartyCode || 'IND'}
                  />
                </div>
                <div className="tcp-results">
                  <div className="tcp-candidate elected">
                    <h4>Elected</h4>
                    <p>{localSelectedElectorate.TwoCandidatePreferred.Elected.GivenNames} {localSelectedElectorate.TwoCandidatePreferred.Elected.FamilyName}</p>
                    <p>Party: {localSelectedElectorate.TwoCandidatePreferred.Elected.PartyCode || 'Independent'}</p>
                    <p>Votes: {formatVotes(localSelectedElectorate.TwoCandidatePreferred.Elected.Votes)}</p>
                    <p>Swing: {formatSwing(localSelectedElectorate.TwoCandidatePreferred.Elected.Swing)}</p>
                  </div>
                  {localSelectedElectorate.TwoCandidatePreferred.Other && (
                    <div className="tcp-candidate">
                      <h4>Other Candidate</h4>
                      <p>{localSelectedElectorate.TwoCandidatePreferred.Other.GivenNames} {localSelectedElectorate.TwoCandidatePreferred.Other.FamilyName}</p>
                      <p>Party: {localSelectedElectorate.TwoCandidatePreferred.Other.PartyCode || 'Independent'}</p>
                      <p>Votes: {formatVotes(localSelectedElectorate.TwoCandidatePreferred.Other.Votes)}</p>
                      <p>Swing: {formatSwing(localSelectedElectorate.TwoCandidatePreferred.Other.Swing)}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="detail-item full-width">
              <strong>Description</strong>
              <div className="description-content">
                <div dangerouslySetInnerHTML={{ __html: cleanDescription(localSelectedElectorate.Description) }} />
              </div>
            </div>

            {localSelectedElectorate.Locations && localSelectedElectorate.Locations.length > 0 && (
              <div className="detail-item full-width">
                <strong>Suburbs</strong>
                <div className="suburbs-section">
                  <ul>
                    {localSelectedElectorate.Locations.flatMap((location, locationIndex) => 
                      location.Localities ? location.Localities.map((suburb, suburbIndex) => (
                        <li key={`${locationIndex}-${suburbIndex}-${suburb}`}>{suburb}</li>
                      )) : []
                    )}
                  </ul>
                </div>
              </div>
            )}

            {localSelectedElectorate.DemographicRating && (
              <div className="detail-item full-width">
                <strong>Demographic Rating</strong>
                <div className="description-content" dangerouslySetInnerHTML={{ __html: cleanDescription(localSelectedElectorate.DemographicRating) }} />
              </div>
            )}

            {localSelectedElectorate.NameDerivation && (
              <div className="detail-item full-width">
                <strong>Name Derivation</strong>
                <div className="description-content" dangerouslySetInnerHTML={{ __html: cleanDescription(localSelectedElectorate.NameDerivation) }} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ElectorateSearch; 